(function () {
  const DIGITISATION_FEE_VARIANT_ID = 54202980827349;

  const FILE_UPLOAD_PROPERTY_NAME = 'Upload New Logo For Digitising';
  const PRIOR_DIGITISATION_PROPERTY_NAME =
    'Have You Ever Had This Exact Logo Digitised With Us Before';
  const FEE_STEM_PROPERTY = '_digitisation_stem';
  const FEE_LABEL_PROPERTY = 'Logo';
  const FEE_IMAGE_PROPERTY = '_digitisation_image';
  const FILE_SIZE_PROPERTY = '_digitisation_file_size';
  const FILE_SIZES_PROPERTY = '_digitisation_file_sizes';
  const OPTIONS_BLOCK_START = '--- Options:';
  const FPOB_UUID_SUFFIX =
    /_[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

  function isDebugEnabled() {
    try {
      return localStorage.getItem('fpob_debug') === '1';
    } catch {
      return false;
    }
  }

  function debugLog(...args) {
    if (isDebugEnabled()) console.log('[FPOB digitisation]', ...args);
  }

  function normalizeStem(name) {
    const stem = String(name).replace(/\.[^/.]+$/, '').trim().toLowerCase();
    return stem
      .replace(/\s+/g, '_')
      .replace(/[^a-z0-9_]/g, '_')
      .replace(/_+/g, '_')
      .replace(/^_|_$/g, '');
  }

  function basenameFromUrl(value) {
    if (value == null || value === '') return '';

    try {
      const url = new URL(String(value), window.location.origin);
      const path = decodeURIComponent(url.pathname);
      return (path.split('/').pop() || '').split('?')[0];
    } catch {
      return String(value).split('/').pop().split('?')[0];
    }
  }

  function extractFpobStemFromUrl(value) {
    let stem = basenameFromUrl(value).replace(/\.[^/.]+$/, '');

    if (stem.toLowerCase().startsWith('fpob_')) {
      stem = stem.slice(5);
    }

    stem = stem.replace(FPOB_UUID_SUFFIX, '');
    return normalizeStem(stem);
  }

  function computeDesignKey(uploadUrl, fileSizeBytes) {
    const stem = extractFpobStemFromUrl(uploadUrl);
    if (!stem) return '';

    const size = Number(fileSizeBytes);
    if (size > 0) return `${stem}__${size}`;
    return stem;
  }

  function parseDesignKey(key) {
    const m = String(key || '').match(/^(.*)__(\d+)$/);
    if (m) return { stem: m[1], size: Number(m[2]) };
    return { stem: String(key || ''), size: 0 };
  }

  function designKeysCompatible(a, b) {
    const pa = parseDesignKey(a);
    const pb = parseDesignKey(b);
    if (!pa.stem || pa.stem !== pb.stem) return false;
    if (pa.size > 0 && pb.size > 0 && pa.size !== pb.size) return false;
    return true;
  }

  function getUploadDisplayLabel(value) {
    return extractFpobStemFromUrl(value).replace(/_/g, ' ');
  }

  function getFileSizeFromProperties(props) {
    const raw = props?.[FILE_SIZE_PROPERTY];
    const size = Number(raw);
    return size > 0 ? size : 0;
  }

  function getFileSizesMapFromProperties(props) {
    const raw = props?.[FILE_SIZES_PROPERTY];
    if (raw == null || raw === '') return {};

    try {
      const parsed = typeof raw === 'string' ? JSON.parse(raw) : raw;
      if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) return {};
      return parsed;
    } catch {
      return {};
    }
  }

  function findLocalFileSizeForUpload(uploadUrl) {
    const urlStem = extractFpobStemFromUrl(uploadUrl);
    if (!urlStem || typeof FPOB === 'undefined') return 0;

    if (FPOB.addedFiles && typeof FPOB.addedFiles === 'object') {
      for (const [filename, file] of Object.entries(FPOB.addedFiles)) {
        const localStem = extractFpobStemFromUrl(filename);
        if (localStem !== urlStem) continue;

        const size = Number(file?.size);
        if (size > 0) return size;
      }
    }

    for (const opt of Object.values(FPOB.selectedOptions || {})) {
      if (opt.optionType !== 'fileUpload') continue;
      if (!opt.name?.startsWith(FILE_UPLOAD_PROPERTY_NAME)) continue;

      for (const value of opt.value || []) {
        if (extractFpobStemFromUrl(value) !== urlStem) continue;
        const size = Number(value?.size);
        if (size > 0) return size;
      }
    }

    return 0;
  }

  function getUploadUrlsFromProperties(props) {
    if (!props) return [];

    const urls = [];
    for (const [key, value] of Object.entries(props)) {
      if (!key.startsWith(FILE_UPLOAD_PROPERTY_NAME)) continue;
      if (value == null || value === '') continue;
      urls.push(String(value));
    }
    return urls;
  }

  function resolveUploadFileSize(uploadUrl, props, uploadCount) {
    const localSize = findLocalFileSizeForUpload(uploadUrl);
    if (localSize > 0) return localSize;

    const stem = extractFpobStemFromUrl(uploadUrl);
    const mapped = Number(getFileSizesMapFromProperties(props)[stem]);
    if (mapped > 0) return mapped;

    // Single stored size belongs to the only upload. Do not stamp it onto every slot.
    if (uploadCount === 1) return getFileSizeFromProperties(props);

    return 0;
  }

  function getUploadsFromProperties(props) {
    const urls = getUploadUrlsFromProperties(props);
    const uploads = [];

    for (const uploadUrl of urls) {
      const fileSize = resolveUploadFileSize(uploadUrl, props, urls.length);
      const designKey = computeDesignKey(uploadUrl, fileSize);
      if (!designKey) continue;

      uploads.push({
        designKey,
        displayLabel: getUploadDisplayLabel(uploadUrl),
        uploadUrl,
      });
    }

    return uploads;
  }

  function enrichItemsWithDigitisationMeta(items) {
    if (!Array.isArray(items)) return items;

    return items.map((item) => {
      if (!item?.properties) return item;

      const props = { ...item.properties };
      const urls = getUploadUrlsFromProperties(props);
      if (!urls.length) return item;

      const sizesMap = { ...getFileSizesMapFromProperties(props) };

      for (const url of urls) {
        const stem = extractFpobStemFromUrl(url);
        if (!stem) continue;
        if (Number(sizesMap[stem]) > 0) continue;

        const localSize = findLocalFileSizeForUpload(url);
        if (localSize > 0) {
          sizesMap[stem] = String(localSize);
          debugLog('injected file size', localSize, 'for', stem);
        }
      }

      if (Object.keys(sizesMap).length) {
        props[FILE_SIZES_PROPERTY] = JSON.stringify(sizesMap);
      }

      if (urls.length === 1 && getFileSizeFromProperties(props) <= 0) {
        const stem = extractFpobStemFromUrl(urls[0]);
        const size = Number(sizesMap[stem]) || findLocalFileSizeForUpload(urls[0]);
        if (size > 0) {
          props[FILE_SIZE_PROPERTY] = String(size);
        }
      }

      return { ...item, properties: props };
    });
  }

  function getCartEndpoints() {
    const themeRoutes = typeof Theme !== 'undefined' ? Theme.routes : null;
    if (themeRoutes?.cart_url) {
      const cartPath = themeRoutes.cart_url.replace(/\/$/, '');
      return {
        cartUrl: `${cartPath}.js`,
        updateUrl: themeRoutes.cart_update_url || `${cartPath}/update.js`,
        addUrl: themeRoutes.cart_add_url || `${cartPath}/add.js`,
        changeUrl: themeRoutes.cart_change_url || `${cartPath}/change.js`,
      };
    }

    const root = settings?.shopify?.routes?.root || '/';
    if (root === '/') {
      return {
        cartUrl: '/cart.js',
        updateUrl: '/cart/update.js',
        addUrl: '/cart/add.js',
        changeUrl: '/cart/change.js',
      };
    }

    const base = root.endsWith('/') ? root : `${root}/`;
    return {
      cartUrl: `${base}cart.js`,
      updateUrl: `${base}cart/update.js`,
      addUrl: `${base}cart/add.js`,
      changeUrl: `${base}cart/change.js`,
    };
  }

  function isFeeVariantConfigured() {
    return Number(DIGITISATION_FEE_VARIANT_ID) > 0;
  }

  function isFeeLine(item) {
    return Number(item?.variant_id) === Number(DIGITISATION_FEE_VARIANT_ID);
  }

  function getFeeLinesByKey(cart) {
    /** @type {Map<string, object[]>} */
    const map = new Map();

    for (const item of cart?.items || []) {
      if (!isFeeLine(item)) continue;
      const designKey = String(item.properties?.[FEE_STEM_PROPERTY] || '').trim();
      if (!designKey) continue;
      if (!map.has(designKey)) map.set(designKey, []);
      map.get(designKey).push(item);
    }

    return map;
  }

  function feeCoversDesignKey(feeByKey, designKey) {
    for (const [feeKey, lines] of feeByKey.entries()) {
      if (!lines?.length) continue;
      if (designKeysCompatible(feeKey, designKey)) return true;
    }
    return false;
  }

  /**
   * @returns {'all' | 'one' | 'none'}
   */
  function normalizePriorDigitisationValue(value) {
    return String(value || '')
      .toLowerCase()
      .replace(/\([^)]*\)/g, ' ')
      .replace(/\s+/g, ' ')
      .trim();
  }

  function isPriorDigitisationPropertyKey(key) {
    const text = String(key || '');
    if (text.startsWith(PRIOR_DIGITISATION_PROPERTY_NAME)) return true;
    return /digitiz[se]d\s+with\s+us\s+before/i.test(text);
  }

  function getPriorDigitisationChoice(props) {
    if (!props) return 'none';

    for (const [key, value] of Object.entries(props)) {
      if (!isPriorDigitisationPropertyKey(key)) continue;
      if (value == null || value === '') continue;

      const text = normalizePriorDigitisationValue(value);
      if (/yes.*\ball\b/.test(text)) return 'all';
      if (/yes.*\bone\b/.test(text)) return 'one';
      return 'none';
    }

    return 'none';
  }

  function isDesignKeyCovered(designKey, requiredMap, feeByKey) {
    if (feeCoversDesignKey(feeByKey, designKey)) return true;

    for (const existingKey of requiredMap.keys()) {
      if (designKeysCompatible(existingKey, designKey)) return true;
    }

    return false;
  }

  function selectUploadsForFees(uploads, choice, isCovered) {
    if (!uploads?.length) return [];

    if (choice === 'all') return [];

    if (choice === 'one') {
      if (uploads.length <= 1) return [];

      const uncovered = uploads.find((upload) => !isCovered(upload.designKey));
      return uncovered ? [uncovered] : [];
    }

    return uploads;
  }

  function collapseRequiredDesignKeys(keys) {
    const entries = [...keys.entries()].map(([key, meta]) => ({ key, meta }));
    /** @type {Map<string, { displayLabel: string, uploadUrl: string }>} */
    const collapsed = new Map();
    const used = new Set();

    for (let i = 0; i < entries.length; i++) {
      if (used.has(i)) continue;

      let best = entries[i];
      used.add(i);

      for (let j = i + 1; j < entries.length; j++) {
        if (used.has(j)) continue;
        if (!designKeysCompatible(best.key, entries[j].key)) continue;

        used.add(j);
        const bestSize = parseDesignKey(best.key).size;
        const otherSize = parseDesignKey(entries[j].key).size;
        if (otherSize > 0 && bestSize <= 0) best = entries[j];
      }

      collapsed.set(best.key, best.meta);
    }

    return collapsed;
  }

  function getRequiredDesignKeys(cart) {
    /** @type {Map<string, { displayLabel: string, uploadUrl: string }>} */
    const keys = new Map();
    const feeByKey = getFeeLinesByKey(cart);

    for (const item of cart?.items || []) {
      if (isFeeLine(item)) continue;

      const uploads = getUploadsFromProperties(item.properties);
      if (!uploads.length) continue;

      const choice = getPriorDigitisationChoice(item.properties);
      const selected = selectUploadsForFees(uploads, choice, (designKey) =>
        isDesignKeyCovered(designKey, keys, feeByKey)
      );

      if (choice === 'none' && uploads.length) {
        const visibleProps = Object.entries(item.properties || {})
          .filter(([key, value]) => !String(key).startsWith('_') && value != null && value !== '')
          .map(([key, value]) => `${key}=${value}`);
        debugLog('prior digitisation choice none; visible props', visibleProps);
      }

      debugLog('prior digitisation choice', choice, {
        uploadKeys: uploads.map((u) => u.designKey),
        selectedKeys: selected.map((u) => u.designKey),
      });

      for (const upload of selected) {
        keys.set(upload.designKey, {
          displayLabel: upload.displayLabel,
          uploadUrl: upload.uploadUrl,
        });
      }
    }

    return collapseRequiredDesignKeys(keys);
  }

  function collectSectionIds() {
    const ids = new Set();

    document.querySelectorAll('cart-items-component[data-section-id]').forEach((el) => {
      if (el.dataset.sectionId) ids.add(el.dataset.sectionId);
    });

    document.querySelectorAll('[data-section-id]').forEach((el) => {
      const id = el.dataset.sectionId;
      if (id && /cart/i.test(id)) ids.add(id);
    });

    return [...ids];
  }

  async function fetchCart() {
    const { cartUrl } = getCartEndpoints();
    const response = await fetch(cartUrl, { credentials: 'same-origin' });
    if (!response.ok) throw new Error(`cart.js ${response.status}`);
    return response.json();
  }

  async function addFeeLine(designKey, displayLabel, uploadUrl) {
    const { addUrl } = getCartEndpoints();
    const sections = collectSectionIds();
    const properties = {
      [FEE_STEM_PROPERTY]: designKey,
      [FEE_LABEL_PROPERTY]: displayLabel || designKey,
    };

    if (uploadUrl) {
      properties[FEE_IMAGE_PROPERTY] = String(uploadUrl);
    }

    const body = {
      items: [
        {
          id: Number(DIGITISATION_FEE_VARIANT_ID),
          quantity: 1,
          properties,
        },
      ],
    };

    if (sections.length) {
      body.sections = sections.join(',');
      body.sections_url = window.location.pathname;
    }

    const response = await fetch(addUrl, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      credentials: 'same-origin',
      body: JSON.stringify(body),
    });

    if (!response.ok) {
      const text = await response.text();
      throw new Error(`cart/add.js ${response.status}: ${text}`);
    }

    return response.json();
  }

  async function changeCartLineQuantity(lineKey, quantity) {
    const { changeUrl } = getCartEndpoints();
    const sections = collectSectionIds();
    const body = {
      id: lineKey,
      quantity,
    };

    if (sections.length) {
      body.sections = sections.join(',');
      body.sections_url = window.location.pathname;
    }

    const response = await fetch(changeUrl, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      credentials: 'same-origin',
      body: JSON.stringify(body),
    });

    if (!response.ok) {
      const text = await response.text();
      throw new Error(`cart/change.js ${response.status}: ${text}`);
    }

    return response.json();
  }

  async function removeCartLine(lineKey) {
    return changeCartLineQuantity(lineKey, 0);
  }

  async function updateCartLineQuantity(lineKey, quantity) {
    return changeCartLineQuantity(lineKey, quantity);
  }

  function dispatchCartRefresh(resource) {
    try {
      document.dispatchEvent(
        new CustomEvent('cart:update', {
          bubbles: true,
          detail: {
            resource,
            sourceId: 'fpob-digitisation-fee',
            data: {
              source: 'fpob-digitisation-fee',
              itemCount: resource?.item_count,
              sections: resource?.sections,
            },
          },
        })
      );
    } catch (error) {
      debugLog('cart:update dispatch failed', error);
    }
  }

  function createReconciler() {
    let reconcileInFlight = false;
    let reconcileQueued = false;

    async function reconcileDigitisationFees() {
      if (!isFeeVariantConfigured()) {
        console.warn(
          '[FPOB digitisation] DIGITISATION_FEE_VARIANT_ID is not set. Create the fee product and update the constant.'
        );
        return false;
      }

      if (reconcileInFlight) {
        reconcileQueued = true;
        return false;
      }

      reconcileInFlight = true;
      let mutated = false;
      let lastMutationResponse = null;

      try {
        let cart = await fetchCart();

        for (const item of cart?.items || []) {
          if (!isFeeLine(item)) continue;
          if (Number(item.quantity) > 1) {
            debugLog('reset fee line quantity', item.key, '-> 1');
            lastMutationResponse = await updateCartLineQuantity(item.key, 1);
            mutated = true;
          }
        }

        if (mutated) {
          cart = await fetchCart();
        }

        const required = getRequiredDesignKeys(cart);
        let feeByKey = getFeeLinesByKey(cart);

        debugLog('required design keys', [...required.keys()]);
        debugLog(
          'fee keys',
          [...feeByKey.keys()].map((key) => `${key}×${feeByKey.get(key).length}`)
        );

        const allFeeLines = [];
        for (const [designKey, lines] of feeByKey.entries()) {
          for (const line of lines) {
            allFeeLines.push({ designKey, line });
          }
        }

        const claimedLineKeys = new Set();

        for (const [requiredKey] of required) {
          const matches = allFeeLines.filter(
            (entry) =>
              !claimedLineKeys.has(entry.line.key) &&
              designKeysCompatible(entry.designKey, requiredKey)
          );
          if (!matches.length) continue;

          const exact = matches.find((entry) => entry.designKey === requiredKey);
          const keep = exact || matches[0];
          claimedLineKeys.add(keep.line.key);

          for (const entry of matches) {
            if (entry.line.key === keep.line.key) continue;
            debugLog('remove duplicate fee', entry.designKey, entry.line.key);
            lastMutationResponse = await removeCartLine(entry.line.key);
            mutated = true;
          }
        }

        for (const entry of allFeeLines) {
          if (claimedLineKeys.has(entry.line.key)) continue;
          debugLog('remove orphan fee', entry.designKey, entry.line.key);
          lastMutationResponse = await removeCartLine(entry.line.key);
          mutated = true;
        }

        if (mutated) {
          cart = await fetchCart();
          feeByKey = getFeeLinesByKey(cart);
        }

        for (const [designKey, meta] of required) {
          if (feeCoversDesignKey(feeByKey, designKey)) continue;

          debugLog('add fee line', designKey);
          lastMutationResponse = await addFeeLine(
            designKey,
            meta.displayLabel,
            meta.uploadUrl
          );
          mutated = true;
        }

        if (mutated) {
          const refreshed = lastMutationResponse?.items
            ? lastMutationResponse
            : await fetchCart();
          dispatchCartRefresh(refreshed);
        }

        return mutated;
      } catch (error) {
        console.warn('FPOB: digitisation fee reconcile failed', error);
        return false;
      } finally {
        reconcileInFlight = false;

        if (reconcileQueued) {
          reconcileQueued = false;
          queueReconcile(50);
        }
      }
    }

    function queueReconcile(delayMs = 200) {
      window.clearTimeout(window.__fpobDigitisationReconcileTimer);
      window.__fpobDigitisationReconcileTimer = window.setTimeout(() => {
        reconcileDigitisationFees();
      }, delayMs);
    }

    return { reconcileDigitisationFees, queueReconcile };
  }

  function getActiveProduct() {
    return window.fpob_active_product || window.product || null;
  }

  function resolveVariantForItem(item) {
    const variantId = Number(item?.id ?? item?.variant_id);
    if (!variantId) return null;

    const product = getActiveProduct();
    const variants = product?.variants;
    if (!Array.isArray(variants)) return null;

    return (
      variants.find((variant) => Number(variant?.id) === variantId) || null
    );
  }

  function formatVariantLabel(variant) {
    if (!variant) return '';

    const options = [variant.option1, variant.option2, variant.option3]
      .map((value) => (value == null ? '' : String(value).trim()))
      .filter((value) => value && value.toLowerCase() !== 'default title');

    if (options.length) return options.join(' / ');

    const title = String(variant.title || '').trim();
    if (title && title.toLowerCase() !== 'default title') return title;

    return '';
  }

  function formatOptionsHeader(productTitle, variantLabel, sku, qty) {
    let header = productTitle || 'Product';
    const label = String(variantLabel || '').trim();
    if (label && label.toLowerCase() !== 'default title') {
      header += ` - ${label}`;
    }

    const skuText = String(sku || '').trim();
    if (skuText) header += ` | SKU: ${skuText}`;
    header += ` (qty ${qty})`;

    return `${OPTIONS_BLOCK_START} ${header} ---`;
  }

  function hasVisibleProperties(props) {
    if (!props) return false;
    return Object.entries(props).some(
      ([key, value]) => !key.startsWith('_') && value != null && value !== ''
    );
  }

  function stripFpobOptionsBlocks(note) {
    const text = String(note || '').trim();
    if (!text) return '';

    const kept = text
      .split(/\n\n+/)
      .map((block) => block.trim())
      .filter((block) => block && !block.startsWith(OPTIONS_BLOCK_START));

    return kept.join('\n\n').trim();
  }

  function formatCartItemOptionsBlock(item) {
    if (!item?.properties || isFeeLine(item)) return '';
    if (!hasVisibleProperties(item.properties)) return '';

    const qty = Number(item.quantity) || 1;
    const productTitle =
      item.product_title || getActiveProduct()?.title || 'Product';

    let variantLabel = String(item.variant_title || '').trim();
    let sku = String(item.sku || '').trim();

    if (!variantLabel || !sku) {
      const variant = resolveVariantForItem(item);
      if (!variantLabel) variantLabel = formatVariantLabel(variant);
      if (!sku) sku = String(variant?.sku || '').trim();
    }

    const lines = [formatOptionsHeader(productTitle, variantLabel, sku, qty)];

    for (const [key, value] of Object.entries(item.properties)) {
      if (key.startsWith('_') || value == null || value === '') continue;
      lines.push(`${key}: ${value}`);
    }

    return lines.length > 1 ? lines.join('\n') : '';
  }

  function buildOptionsNoteFromCart(cart) {
    const blocks = [];

    for (const item of cart?.items || []) {
      const block = formatCartItemOptionsBlock(item);
      if (block) blocks.push(block);
    }

    return blocks.join('\n\n');
  }

  let noteSyncInFlight = false;
  let noteSyncQueued = false;

  function queueNoteSync(delayMs = 250) {
    window.clearTimeout(window.__fpobOptionsNoteSyncTimer);
    window.__fpobOptionsNoteSyncTimer = window.setTimeout(() => {
      syncCartOptionsNote();
    }, delayMs);
  }

  /**
   * Rebuild FPOB Options section of cart.note from current cart lines.
   * Clears orphaned Options left by abandoned checkouts when cart is empty/changed.
   */
  async function syncCartOptionsNote() {
    if (noteSyncInFlight) {
      noteSyncQueued = true;
      return false;
    }

    noteSyncInFlight = true;

    try {
      const cart = await fetchCart();
      const leftover = stripFpobOptionsBlocks(cart.note);
      const optionsNote = buildOptionsNoteFromCart(cart);
      const nextNote = [leftover, optionsNote].filter(Boolean).join('\n\n');
      const previousNote = String(cart.note || '').trim();

      if (nextNote === previousNote) {
        debugLog('cart options note already in sync');
        return false;
      }

      const { updateUrl } = getCartEndpoints();
      await fetch(updateUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'same-origin',
        body: JSON.stringify({ note: nextNote }),
      });

      debugLog('cart options note synced', {
        optionBlocks: optionsNote ? optionsNote.split(/\n\n+/).length : 0,
        clearedOrphans: previousNote.includes(OPTIONS_BLOCK_START) && !optionsNote,
      });

      return true;
    } catch (error) {
      console.warn('FPOB: failed to sync options cart note', error);
      return false;
    } finally {
      noteSyncInFlight = false;

      if (noteSyncQueued) {
        noteSyncQueued = false;
        queueNoteSync(50);
      }
    }
  }

  function ensureReconcilerHooks() {
    if (window.__fpobDigitisationReconcilerApplied) {
      return window.__fpobDigitisationApi;
    }

    const api = createReconciler();
    window.__fpobDigitisationApi = api;
    window.__fpobDigitisationReconcilerApplied = true;

    document.addEventListener(
      'cart:update',
      (event) => {
        if (event.detail?.sourceId === 'fpob-digitisation-fee') return;
        if (event.detail?.data?.source === 'fpob-digitisation-fee') return;
        if (event.detail?.sourceId === 'fpob-options-note') return;
        if (event.detail?.data?.source === 'fpob-options-note') return;
        api.queueReconcile(250);
        queueNoteSync(300);
      },
      true
    );

    document.addEventListener(
      'cart:change',
      () => {
        api.queueReconcile(250);
        queueNoteSync(300);
      },
      true
    );

    api.queueReconcile(400);
    queueNoteSync(450);
    debugLog('reconciler hooks installed');
    return api;
  }

  function tryPatchFpobAtc(api) {
    if (window.__fpobDigitisationAtcPatched) return true;
    if (typeof FPOB === 'undefined' || typeof FPOB.atc !== 'function') return false;

    const originalAtc = FPOB.atc.bind(FPOB);

    FPOB.atc = async function (items, isAddToCart) {
      const enrichedItems = enrichItemsWithDigitisationMeta(items);
      const result = await originalAtc(enrichedItems, isAddToCart);

      if (isAddToCart !== false) {
        if (result === undefined) {
          await new Promise((resolve) => setTimeout(resolve, 500));
        }

        try {
          await syncCartOptionsNote();
        } catch (error) {
          console.warn('FPOB: failed to sync options cart note after ATC', error);
        }

        try {
          await api.reconcileDigitisationFees();
        } catch (error) {
          console.warn('FPOB: digitisation fee reconcile after ATC failed', error);
        }
      }

      return result;
    };

    window.__fpobDigitisationAtcPatched = true;
    debugLog('FPOB.atc patched');
    return true;
  }

  const api = ensureReconcilerHooks();

  if (!tryPatchFpobAtc(api)) {
    let attempts = 0;
    const timer = window.setInterval(() => {
      attempts += 1;
      if (tryPatchFpobAtc(api) || attempts >= 40) {
        window.clearInterval(timer);
      }
    }, 250);
  }
})();
